"use client";
import React, { ChangeEvent, ReactElement, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { notFound, useRouter, useSearchParams } from "next/navigation";

import CanvasPane from "@/components/editor/canvas-pane";
import EditorFileMenu from "@/components/editor/editor-file-menu";
import EditorLoading from "@/components/editor/editor-loading";
import EditorViewMenu from "@/components/editor/editor-view-menu";
import FramePicker from "@/components/editor/frame-picker/frame-picker";
import LocalSaveStatus from "@/components/editor/local-save-status";
import Palette from "@/components/editor/palette";
import PublicGallerySwitch from "@/components/editor/public-gallery-switch";
import Timeline from "@/components/editor/timeline";
import Toolbar from "@/components/editor/toolbar";
import ChooseAnimationDimensionsModal from "@/components/modal/components/choose-animation-dimensions";
import { AnimationType, PixelMode } from "@/gql/generated/graphql";
import { useKeyboardShortcuts } from "@/hooks/editor/editor-keyboard-hooks";
import { useAutosave } from "@/hooks/editor/use-autosave";
import {
  BOOTSTRAP_ERRORS,
  useEditorBootstrap,
} from "@/hooks/editor/use-editor-bootstrap";
import { useModal } from "@/hooks/use-modal";
import { backgroundGradient } from "@/lib/css/background-gradient";
import * as animationActions from "@/lib/state/actions/animation-actions";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { animationSelector, editorSelector } from "@/lib/state/selectors";
import { useAppDispatch } from "@/lib/state/store";

import "./_editor.scss";

export default function Editor() {
  const playbackTimer = useRef<NodeJS.Timeout>(undefined);

  const dispatch = useAppDispatch();
  const editor = useSelector(editorSelector);
  const animation = useSelector(animationSelector);
  const { openModal } = useModal();
  const searchParams = useSearchParams();
  const { loading, error } = useEditorBootstrap();
  const cancelPendingAutosave = useAutosave({
    enabled: !loading,
  });
  const router = useRouter();
  useKeyboardShortcuts();

  const createNewAnimation = searchParams.get("new") === "true";
  const createNewAvatar = searchParams.get("newAvatar") === "true";

  useEffect(() => {
    if (editor.mode !== "playback") {
      clearTimeout(playbackTimer.current);
      return;
    }

    let nextSequenceIndex = editor.currentFrameIndex + 1;
    if (nextSequenceIndex === animation.sequence.length) {
      nextSequenceIndex = 0;
    }

    playbackTimer.current = setTimeout(() => {
      dispatch(editorActions.goToFrame(nextSequenceIndex));
    }, animation.sequence[editor.currentFrameIndex].delay);

    return function cleanup() {
      clearTimeout(playbackTimer?.current);
    };
  }, [animation.sequence, dispatch, editor.currentFrameIndex, editor.mode]);

  const onAnimationTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(animationActions.setAnimationTitle(event.target.value));
  };

  // Trigger new animation modal if ?new=true is in the url
  useEffect(() => {
    if (createNewAnimation && !loading) {
      openModal(() => <ChooseAnimationDimensionsModal />);
    }
  }, [createNewAnimation, openModal, loading]);

  // Trigger new avatar if ?newAvatar=true is in the url
  useEffect(() => {
    const createAvatar = async () => {
      await dispatch(
        editorActions.createNew({
          width: 128,
          height: 128,
          pixelMode: PixelMode.Pixelated,
          type: AnimationType.Avatar,
        }),
      );
      router.replace("/editor");
    }
    if (createNewAvatar) {
      createAvatar();
    }
  }, [createNewAvatar, openModal, loading, dispatch, router]);

  if (loading) return <EditorLoading />;
  if (error === BOOTSTRAP_ERRORS.NOT_FOUND) {
    notFound();
  } else if (error) {
    return <p>Error</p>;
  }

  return (
    <div
      className="editor"
      style={backgroundGradient(
        animation.palette[editor.currentColourIndex],
        animation.palette[editor.currentColourIndex],
      )}
    >
      <div className="editor-options-bar flex items-center justify-between px-2">
        <EditorFileMenu />
        <EditorViewMenu />

        <div className="flex items-center justify-center flex-auto">
          <input
            className="text-sm rounded-md w-40 shadow-inner p-1 border border-gray-400"
            type="text"
            value={animation.title}
            onChange={onAnimationTitleChanged}
            maxLength={50}
          />
          {animation.type === AnimationType.Standalone ? (
            <PublicGallerySwitch
              isPublic={animation.public}
              onAnimationPublicToggled={() => {
                dispatch(
                  animationActions.setAnimationPublic(!animation.public),
                );
              }}
            />
          ) : (
            <span className="ml-1">Profile picture</span>
          )}
          <LocalSaveStatus />
        </div>
      </div>

      <div className="editor-toolbar-container select-none">
        <Toolbar key="toolbar" />
        <Palette
          key="palette"
          visible={editor.mode !== "playback" && editor.currentTool !== "erase"}
        />
      </div>

      {animation.sequence[editor.currentFrameIndex] && (
        <CanvasPane
          layerVisibility={editor.layerVisibility}
          currentLayerIndex={editor.currentLayerIndex}
          sequence={animation.sequence}
          currentFrameIndex={editor.currentFrameIndex}
          width={animation.width}
          height={animation.height}
          currentTool={editor.currentTool}
          cancelPendingAutosave={cancelPendingAutosave}
          pixelMode={animation.pixelMode}
        />
      )}

      <FramePicker />

      <div className="editor-bottom select-none">
        <Timeline />
      </div>
    </div>
  );
};

